import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {UiQuestionnaireLayout, Col, UiText, Row, UiButton} from '~/components/elements';
import {AMENORRHOEA_TEST_KEYS} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer, setNextQuestionnaire} from '~/redux/questionnaires/slice';
import {Questionnaire} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';

const Questionnaire4Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === AMENORRHOEA_TEST_KEYS.APPOINTMENT_PREFERENCES);
  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[AMENORRHOEA_TEST_KEYS.APPOINTMENT_PREFERENCES]?.data,
  );
  const selectedTimes = currentAnswer?.selectedTimes || [];
  const [selectedDays, setSelectedDays] = useState<Questionnaire['dayOptions']>(
    questionnaire?.dayOptions?.map(day => {
      const dayIsSelected = selectedTimes?.some((selectedDay: any) => selectedDay.day === day.value);
      return {
        ...day,
        selected: dayIsSelected,
        timeOptions: day.timeOptions.map(time => {
          const timeIsSelected = selectedTimes?.some((selectedDay: any) => {
            return selectedDay.day === day.value && selectedDay.selectedTimes.includes(time.value);
          });
          return {
            ...time,
            selected: timeIsSelected,
          };
        }),
      };
    }) as Questionnaire['dayOptions'],
  );
  const {inputs, setInputs} = useInput({
    clinicId: currentAnswer?.clinicId || null,
  });
  const filteredSelectedDays = selectedDays?.filter(day => day.selected);
  const isDisable =
    inputs?.clinicId === null ||
    inputs?.clinicId === undefined ||
    !filteredSelectedDays?.length ||
    filteredSelectedDays?.some(val => val.timeOptions.every(el => !el.selected));

  useEffect(() => {
    if (questionnaire?.options?.length === 1) {
      setInputs({clinicId: questionnaire?.options[0].clinicId});
    }
  }, [questionnaire?.options, setInputs]);

  const onClickSubmit = () => {
    const selectedDayValues = filteredSelectedDays?.map(({value: dayValue}) => ({day: dayValue}));
    const selectedTimeValues = filteredSelectedDays?.map(({value: dayValue, dayOfWeek, timeOptions}) => ({
      day: dayValue,
      dayOfWeek,
      selectedTimes: timeOptions?.filter(time => time?.selected)?.map(({value: timeValue}) => timeValue),
    }));

    dispatch(
      setQuestionnaireAnswer({
        key: AMENORRHOEA_TEST_KEYS.APPOINTMENT_PREFERENCES,
        index: questionnaire?.index || '',
        data: {
          ...inputs,
          selectedDays: selectedDayValues,
          selectedTimes: selectedTimeValues,
        },
      }),
    );
    return dispatch(setNextQuestionnaire({value: AMENORRHOEA_TEST_KEYS.YOUR_TEST_PACKAGE}));
  };

  const handleSelectedDay = (day: any, dayIndex: number) => {
    setSelectedDays(prevDays => {
      const newDays = [...prevDays];
      newDays[dayIndex] = {...day, selected: !day.selected};
      return newDays;
    });
  };

  const handleSelectedTime = (dayIndex: number, timeIndex: number) => {
    setSelectedDays(prevDays => {
      const newDays = [...prevDays];
      newDays[dayIndex] = {
        ...newDays[dayIndex],
        timeOptions: newDays[dayIndex].timeOptions.map((time: any, index: number) =>
          index === timeIndex ? {...time, selected: !time.selected} : time,
        ),
      };
      return newDays;
    });
  };

  const renderTimes = (value: any, dayIndex: number, isSelected?: boolean) => {
    if (isSelected) {
      return (
        <Row key={value.label} align="center" justifyContent="center" w="100%" mb={5}>
          <UiText w="60px" fontWeight={600} fontSize={16} color="fadedJade">
            {value.label.substring(0, 3)}
          </UiText>
          <Row flexWrap={'wrap'}>
            {value.timeOptions.map((timeOption: any, timeIndex: number) => (
              <UiButton
                w={{base: '85px', md: '120px'}}
                fontSize={{base: '12px', md: '14px'}}
                onClick={() => handleSelectedTime(dayIndex, timeIndex)}
                key={timeOption.label}
                title={timeOption.label}
                variant={timeOption.selected ? 'solid' : 'outlinePrimary'}
                mr={2}
                mb={2}
                minW="48px"
              />
            ))}
          </Row>
        </Row>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      handleOnNext={onClickSubmit}
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      disableNext={isDisable}
    >
      <Col align="center" textAlign="center">
        <UiText variant="mdqTitle" mt={10} my={5}>
          {questionnaire?.questions?.default}
        </UiText>
        {questionnaire?.descriptions?.default?.map(desc => (
          <UiText key={desc} fontWeight={400} fontSize={14}>
            {desc}
          </UiText>
        ))}
        <UiText fontWeight={600} fontSize={14} my={5}>
          {questionnaire?.questions?.sub}
        </UiText>
        <Row justify="center" w="100%">
          {questionnaire?.options?.map(value => (
            <UiButton
              onClick={() => setInputs({clinicId: value?.clinicId})}
              key={`${value.label}-${value.value}`}
              title={value?.label}
              variant={
                questionnaire?.options?.length &&
                questionnaire?.options.length > 1 &&
                inputs?.clinicId === value.clinicId
                  ? 'solid'
                  : 'clear'
              }
              ml={2}
              color="fadedJade"
              fontSize="18px"
            />
          ))}
        </Row>
        <UiText fontWeight={600} fontSize={14} my={5}>
          {questionnaire?.questions?.dayQuestion}
        </UiText>
        <Row justify="center" flexWrap="wrap" w="100%">
          {selectedDays.map((day: any, index) => (
            <UiButton
              onClick={() => handleSelectedDay(day, index)}
              key={day.label}
              title={day.label.substring(0, 3)}
              variant={day.selected ? 'solid' : 'outlinePrimary'}
              mr={2}
              w={{base: '70px', md: '120px'}}
              fontSize={{base: '12px', md: '14px'}}
              mb={5}
              minW="48px"
            />
          ))}
        </Row>
        <UiText fontWeight={600} fontSize={14} my={5}>
          {questionnaire?.questions?.timeQuestion}
        </UiText>
        {selectedDays.map((day, dayIndex) => renderTimes(day, dayIndex, day?.selected))}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire4Screen;
